import { useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from '@/hooks/useTranslation';
import { useTurnstile } from '@/hooks/useTurnstile';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref'] as const;

const Section = ({ title }: { title: string }) => (
  <div className="text-xs font-bold uppercase tracking-[0.16em] mt-6 mb-3" style={{ fontFamily: 'var(--f-mono)', color: 'var(--blue)' }}>{title}</div>
);

const CheckGroup = ({ label, options, value, onChange }: { label: string; options: { value: string; label: string }[]; value: string[]; onChange: (v: string) => void }) => (
  <div className="f-group">
    <label className="f-label">{label}</label>
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            aria-pressed={on}
            className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
            style={{ background: on ? 'var(--blue)' : 'var(--white)', color: on ? 'var(--white)' : 'var(--slate)', border: `1px solid ${on ? 'var(--blue)' : 'var(--line-slate)'}` }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const CxAssessmentForm = () => {
  const { t, language } = useTranslation();
  const f = t.cx.assessment.form;
  const location = useLocation();

  const [companyName, setCompanyName] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [country, setCountry] = useState('');
  const [contactName, setContactName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [fteBand, setFteBand] = useState('');
  const [languages, setLanguages] = useState<string[]>(['de']);
  const [channels, setChannels] = useState<string[]>([]);
  const [currentModel, setCurrentModel] = useState('');
  const [timeline, setTimeline] = useState('');
  const [message, setMessage] = useState('');
  const [consent, setConsent] = useState(false);

  // ANTI-SPAM 1: honeypot — named "fax" because "company" is a real field on this form.
  const [fax, setFax] = useState('');
  // ANTI-SPAM 2: time-to-submit.
  const renderedAt = useRef<number>(Date.now());
  // ANTI-SPAM 3: Turnstile.
  const { token, widgetRef, reset: resetTurnstile, enabled: turnstileEnabled } = useTurnstile();

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const utm = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out: Record<string, string> = {};
    UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) out[k] = v; });
    return out;
  }, [location.search]);

  const toggle = (list: string[], set: (v: string[]) => void, value: string) =>
    set(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fax) return; // honeypot tripped — silently ignore
    if (turnstileEnabled && !token) {
      toast.error(f.verify);
      return;
    }
    if (!consent) return;

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-cx-assessment', {
        body: {
          companyName, website, industry, country,
          contactName, jobTitle, email, phone,
          fteBand, languages, channels, currentModel, timeline, message,
          consent, language,
          pagePath: location.pathname + location.search,
          utm,
          fax,                                    // honeypot — must be empty
          elapsedMs: Date.now() - renderedAt.current,
          turnstileToken: token,
        },
      });
      if (error) throw error;
      if (data && typeof data === 'object' && 'error' in data && (data as { error?: string }).error) {
        throw new Error((data as { error: string }).error);
      }
      setSent(true);
      resetTurnstile();
    } catch (err) {
      console.error('submit-cx-assessment failed:', err);
      toast.error(f.error);
      resetTurnstile();
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="contact-form-wrap" style={{ textAlign: 'center' }}>
        <h3 className="form-title">{f.title}</h3>
        <p style={{ color: 'var(--blue)', fontWeight: 700, lineHeight: 1.7 }}>{f.success}</p>
      </div>
    );
  }

  return (
    <div className="contact-form-wrap">
      <h3 className="form-title">{f.title}</h3>
      <form onSubmit={handleSubmit} noValidate={false}>
        <Section title={f.companySection} />
        <div className="f-row">
          <div className="f-group">
            <label className="f-label">{f.companyName} *</label>
            <input className="f-input" type="text" required maxLength={200} value={companyName} onChange={(e) => setCompanyName(e.target.value)} autoComplete="organization" />
          </div>
          <div className="f-group">
            <label className="f-label">{f.website}</label>
            <input className="f-input" type="text" maxLength={300} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="www.example.de" autoComplete="url" />
          </div>
        </div>
        <div className="f-row">
          <div className="f-group">
            <label className="f-label">{f.industry} *</label>
            <select className="f-select" required value={industry} onChange={(e) => setIndustry(e.target.value)}>
              {f.industryOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">{f.country}</label>
            <input className="f-input" type="text" maxLength={100} value={country} onChange={(e) => setCountry(e.target.value)} autoComplete="country-name" />
          </div>
        </div>

        <Section title={f.contactSection} />
        <div className="f-row">
          <div className="f-group">
            <label className="f-label">{f.contactName} *</label>
            <input className="f-input" type="text" required maxLength={120} value={contactName} onChange={(e) => setContactName(e.target.value)} autoComplete="name" />
          </div>
          <div className="f-group">
            <label className="f-label">{f.jobTitle}</label>
            <input className="f-input" type="text" maxLength={120} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} autoComplete="organization-title" />
          </div>
        </div>
        <div className="f-row">
          <div className="f-group">
            <label className="f-label">{f.email} *</label>
            <input className="f-input" type="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </div>
          <div className="f-group">
            <label className="f-label">{f.phone}</label>
            <input className="f-input" type="tel" maxLength={40} value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
          </div>
        </div>

        <Section title={f.operationSection} />
        <div className="f-row">
          <div className="f-group">
            <label className="f-label">{f.fteBand} *</label>
            <select className="f-select" required value={fteBand} onChange={(e) => setFteBand(e.target.value)}>
              {f.fteOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="f-group">
            <label className="f-label">{f.currentModel} *</label>
            <select className="f-select" required value={currentModel} onChange={(e) => setCurrentModel(e.target.value)}>
              {f.modelOptions.map((o) => (
                <option key={o.value} value={o.value} disabled={o.value === ''}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <CheckGroup label={f.languages} options={f.languageOptions} value={languages} onChange={(v) => toggle(languages, setLanguages, v)} />
        <CheckGroup label={f.channels} options={f.channelOptions} value={channels} onChange={(v) => toggle(channels, setChannels, v)} />
        <div className="f-group">
          <label className="f-label">{f.timeline} *</label>
          <select className="f-select" required value={timeline} onChange={(e) => setTimeline(e.target.value)}>
            {f.timelineOptions.map((o) => (
              <option key={o.value} value={o.value} disabled={o.value === ''}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="f-group">
          <label className="f-label">{f.message}</label>
          <textarea className="f-textarea" maxLength={4000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder={f.messagePlaceholder} />
        </div>

        <div className="f-group">
          <label className="flex items-start gap-3 text-sm leading-relaxed cursor-pointer" style={{ color: 'var(--slate)' }}>
            <input type="checkbox" required checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
            <span>{f.consent} <Link to="/privacy" className="underline" style={{ color: 'var(--blue)' }}>{f.consentLink}</Link>.</span>
          </label>
        </div>

        {/* ANTI-SPAM 1: honeypot field. Hidden from humans and screen readers. */}
        <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }}>
          <label>
            Fax (leave this field empty)
            <input type="text" name="fax" tabIndex={-1} autoComplete="off" value={fax} onChange={(e) => setFax(e.target.value)} />
          </label>
        </div>

        {/* ANTI-SPAM 3: Turnstile widget renders here. */}
        <div className="f-group" style={{ marginTop: 8 }} ref={widgetRef} />

        <button type="submit" className="f-submit" disabled={sending}>
          {sending ? f.sending : f.submit}
        </button>
        <p className="text-xs mt-3" style={{ color: 'var(--slate-light)' }}>* {f.required}</p>
      </form>
    </div>
  );
};

export default CxAssessmentForm;
